import { Controller } from '@hotwired/stimulus'

export default class BiometricAuthController extends Controller {
  static values = {
    autoLogin: Boolean,
    signInWithTokenUrl: String,
    oauthTokenUrl: String,
    loginWithFaceidLabel: String,
    loginWithTouchidLabel: String,
    loginWithBiometricLabel: String,
  }
  static targets = ['biometricLoginButton'];
  static DEBUG_MODE = true;

  async connect() {
    this._debug('BiometricAuthController connected');
    const biometricAvailable = await this._checkBiometricAvailability();
    if (biometricAvailable && biometricAvailable.available) {
      this.biometricMethods = {
        faceId: this.loginWithFaceidLabelValue,
        touchId: this.loginWithTouchidLabelValue,
      };
      const buttonLabel = this.biometricMethods[biometricAvailable.type] ?? this.loginWithBiometricLabelValue;
      this.biometricLoginButtonTarget.classList.remove('d-none');
      this.biometricLoginButtonTarget.innerHTML = buttonLabel;
      if (this.autoLoginValue) {
        this.authenticateWithSavedTokens();
      }
    }
  }

  async authenticateWithSavedTokens() {
    const data = await this._getSavedOauthTokens();
    this._debug('Saved oauth tokens: ' + JSON.stringify(data));
    if (data.success && data.tokens) {
      const tokens = data.tokens;
      const authResult = await this._authenticateWithTokens(tokens);
      if (authResult) {
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }
    }
  }

  async _checkBiometricAvailability() {
    try {
      const result = await Median.auth.status();
      this._debug('Biometric availability result: ' + JSON.stringify(result));
      if (result && result.hasSecret) {
        return {
          available: result.hasTouchId,
          type: result.biometryType, // 'touchId', 'faceId', or 'none'
        };
      }
    } catch (error) {
      this._debug('Error checking biometric auth availability: ' + error);
    }
    return false;
  }

  async _getSavedOauthTokens() {
    return new Promise((resolve) => {
      Median.auth.get({
        callbackFunction: (data) => {
          let tokens = null;
          if (data && data.secret) {
            try {
              tokens = JSON.parse(data.secret)
            } catch (error) {
              return resolve({success: false, error: error});
            }
          }
          resolve({success: true, tokens: tokens});
        }
      })
    })
  }

  async _authenticateWithTokens(tokens) {
    try {
      const response = await fetch(this.signInWithTokenUrlValue, {
        method: 'POST',
        body: JSON.stringify({access_token: tokens.access_token}),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        this._debug('Successfully authenticated with biometric token: ' + JSON.stringify(data));
        if (data.success) {
          return true;
        }
      } else if (response.status === 401) {
        this._debug('Biometric token is invalid or expired');
        const newTokens = await this._refreshTokens(tokens.refresh_token, tokens.client_uid);
        if (newTokens) {
          return await this._authenticateWithTokens(newTokens);
        }
      } else {
        this._debug('Failed to get oauth tokens, status: ' + response.status);
        return false;
      }
    } catch (error) {
      this._debug('Error getting oauth tokens: ' + error);
      return false;
    }
  }

  async _refreshTokens(refreshToken, clientUid) {
    try {
      const response = await fetch(this.oauthTokenUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          grant_type: 'refresh_token',
          client_id: clientUid,
          refresh_token: refreshToken
        }),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        this._debug('Successfully refreshed tokens: ' + JSON.stringify(data));
        const tokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          client_uid: clientUid,
        };
        Median.auth.save({secret: JSON.stringify(tokens)});
        return tokens;
      } else {
        this._debug('Failed to refresh tokens, status: ' + response.status);
      }
    } catch (error) {
      this._debug('Error refreshing tokens: ' + error);
    }

    Median.auth.delete();
    return false;
  }

  _debug(message) {
    if (this.constructor.DEBUG_MODE) {
      console.log(message);
    }
  }
}
